import { Box, HStack } from "@chakra-ui/react";
import CountTag from "./CountTag";

/**
 * Tabs — underline tab bar (`.tabs` + `.tabs__cell`).
 * design ref: styles.css:584–616, tasks.jsx:45–52
 *
 * Pattern: an HStack with a 1px bottom border (neutral.200). Each cell is
 * absolutely flush with the bottom edge using `paddingBottom: 8px` +
 * `marginBottom: -1px`, so the active cell's inset box-shadow underline
 * paints exactly over the container border. The button itself remains a
 * rounded hit-target — only the wrapping cell carries the underline.
 *
 * Used by TasksPage and MyAccess.
 */

export interface TabItem<K extends string = string> {
  key: K;
  label: string;
  /** Optional trailing count. Renders a <CountTag>. */
  count?: number;
  /** When true, the tab is omitted from render. */
  hidden?: boolean;
}

export interface TabsProps<K extends string = string> {
  tabs: TabItem<K>[];
  activeKey: K;
  onChange: (key: K) => void;
}

const Tabs = <K extends string = string>({
  tabs,
  activeKey,
  onChange,
}: TabsProps<K>) => {
  const visible = tabs.filter((t) => !t.hidden);
  return (
    <HStack
      align="center"
      spacing="2xs" // 4px gap between cells (styles.css:585)
      borderBottomWidth="1px"
      borderBottomStyle="solid"
      borderBottomColor="neutral.200"
      mb="1xl" // 24px (styles.css:587)
    >
      {visible.map((tab) => {
        const isActive = tab.key === activeKey;
        return (
          <Box
            key={tab.key}
            pb="sm" // 8px (styles.css:590)
            mb="-1px" // overlaps the container's bottom border
            // styles.css:594 — active cell paints the azurite underline via
            // an inset box-shadow so it sits flush with the row's border.
            boxShadow={
              isActive ? "inset 0 -1px 0 var(--chakra-colors-p-600)" : "none"
            }
          >
            <Box
              as="button"
              type="button"
              onClick={() => onChange(tab.key)}
              display="inline-flex"
              alignItems="center"
              gap="sm" // 8px (styles.css:597)
              px="10px" // styles.css:598 (no spacing token at 10px)
              py="1xs" // 6px (styles.css:598)
              borderRadius="component.lg" // 8px (styles.css:599)
              bg="transparent"
              fontFamily="body"
              fontWeight={600}
              fontSize="md" // 14px (styles.css:603)
              lineHeight="1"
              color={isActive ? "neutral.950" : "neutral.700"}
              cursor="pointer"
              transition="background-color 120ms, color 120ms"
              _hover={{
                bg: "neutral.100",
                color: "neutral.950",
              }}
              _focusVisible={{
                outline: "none",
                boxShadow: "focusRing",
              }}
            >
              <Box as="span">{tab.label}</Box>
              {tab.count !== undefined && <CountTag count={tab.count} />}
            </Box>
          </Box>
        );
      })}
    </HStack>
  );
};

export default Tabs;
